import { useState } from 'react'
import { Button, Chip, Alert, Progress, KpiCard } from '@/components/ui'
import { Upload, FileText, FileImage, FileSpreadsheet, Trash2, RefreshCw, Search, CheckCircle2, Clock } from 'lucide-react'
import { cn } from '@/lib/utils'

interface Doc {
  id: string
  nombre: string
  tipo: 'pdf' | 'md' | 'xlsx' | 'img'
  tamano: string
  chunks: number
  embeddings: number
  estado: 'ok' | 'indexando' | 'error'
  subidoHace: string
}

const docsInicial: Doc[] = [
  { id: 'd1', nombre: 'manual-cuentas-point.md', tipo: 'md', tamano: '12 kB', chunks: 24, embeddings: 24, estado: 'ok', subidoHace: 'hace 2d' },
  { id: 'd2', nombre: 'FAQ-cobranza-v3.pdf', tipo: 'pdf', tamano: '1.2 MB', chunks: 86, embeddings: 86, estado: 'ok', subidoHace: 'hace 5d' },
  { id: 'd3', nombre: 'politica-descuentos-2026.pdf', tipo: 'pdf', tamano: '340 kB', chunks: 42, embeddings: 42, estado: 'ok', subidoHace: 'hace 1 sem' },
  { id: 'd4', nombre: 'razones-sociales.xlsx', tipo: 'xlsx', tamano: '18 kB', chunks: 3, embeddings: 3, estado: 'ok', subidoHace: 'hace 2 sem' },
  { id: 'd5', nombre: 'instructivo-comprobantes.md', tipo: 'md', tamano: '8 kB', chunks: 18, embeddings: 12, estado: 'indexando', subidoHace: 'hace 2 min' },
  { id: 'd6', nombre: 'anexo-plazas-extendido.pdf', tipo: 'pdf', tamano: '2.1 MB', chunks: 0, embeddings: 0, estado: 'error', subidoHace: 'hace 1 h' },
]

const iconos = {
  pdf: FileText,
  md: FileText,
  xlsx: FileSpreadsheet,
  img: FileImage,
}

export function KnowledgeBase() {
  const [docs, setDocs] = useState(docsInicial)
  const [dragging, setDragging] = useState(false)
  const [filtro, setFiltro] = useState('')

  const filtered = docs.filter(d => d.nombre.toLowerCase().includes(filtro.toLowerCase()))
  const totalChunks = docs.reduce((a, d) => a + d.chunks, 0)
  const totalEmbed = docs.reduce((a, d) => a + d.embeddings, 0)

  const reindex = (id: string) => {
    setDocs(prev => prev.map(d => d.id === id ? { ...d, estado: 'indexando', embeddings: 0 } : d))
    setTimeout(() => {
      setDocs(prev => prev.map(d => d.id === id ? { ...d, estado: 'ok', embeddings: d.chunks } : d))
    }, 2000)
  }

  const remove = (id: string) => setDocs(prev => prev.filter(d => d.id !== id))

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KpiCard label="Documentos" value={docs.length} sub="total" icon={<FileText className="w-3.5 h-3.5" />} />
        <KpiCard label="Chunks" value={totalChunks} sub="fragmentos" />
        <KpiCard label="Embeddings" value={totalEmbed} sub={`${Math.round((totalEmbed / Math.max(totalChunks, 1)) * 100)}% indexado`} color="emerald" />
        <KpiCard label="pgvector HNSW" value="OK" sub="recall@10 = 0.94" color="emerald" />
      </div>

      <div
        onDragOver={e => { e.preventDefault(); setDragging(true) }}
        onDragLeave={() => setDragging(false)}
        onDrop={e => {
          e.preventDefault()
          setDragging(false)
          const files = Array.from(e.dataTransfer.files)
          const nuevos: Doc[] = files.map((f, i) => ({
            id: `d-up-${Date.now()}-${i}`,
            nombre: f.name,
            tipo: f.name.endsWith('.pdf') ? 'pdf' : f.name.endsWith('.xlsx') ? 'xlsx' : f.name.match(/\.(png|jpe?g|webp)$/) ? 'img' : 'md',
            tamano: `${(f.size / 1024).toFixed(0)} kB`,
            chunks: 0,
            embeddings: 0,
            estado: 'indexando',
            subidoHace: 'ahora',
          }))
          setDocs(prev => [...nuevos, ...prev])
          setTimeout(() => {
            setDocs(prev => prev.map(d => nuevos.find(n => n.id === d.id) ? { ...d, chunks: 20 + Math.floor(Math.random() * 60), embeddings: 20, estado: 'indexando' } : d))
          }, 400)
        }}
        className={cn(
          'border-2 border-dashed rounded-xl p-6 text-center transition',
          dragging ? 'border-amber-500 bg-amber-50' : 'border-slate-300 bg-slate-50/50'
        )}
      >
        <Upload className={cn('w-10 h-10 mx-auto mb-2', dragging ? 'text-amber-600' : 'text-slate-400')} />
        <div className="font-semibold text-navy-700">Arrastrá archivos acá para indexar</div>
        <div className="text-xs text-slate-500 mt-1">PDF · Markdown · Excel · Imágenes · máx 10 MB por archivo</div>
        <Button variant="amber" className="mt-3">
          <Upload className="w-4 h-4" /> Subir archivos
        </Button>
      </div>

      <Alert variant="tip" title="Cómo funciona">
        Los documentos se parten en chunks y vectorizan (OpenAI embeddings) → pgvector con índice HNSW. El copilot busca los top-k relevantes antes de responder y nunca inventa fuera de este material.
      </Alert>

      <section className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        <header className="px-5 py-3 border-b border-slate-200 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <h2 className="font-display text-lg text-navy-700">Documentos indexados</h2>
            <Chip variant="default">{docs.length}</Chip>
          </div>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input
              value={filtro}
              onChange={e => setFiltro(e.target.value)}
              placeholder="Filtrar…"
              className="pl-9 pr-3 py-1.5 border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-amber-500 bg-white"
            />
          </div>
        </header>
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-600 font-semibold">
            <tr>
              <th className="text-left px-5 py-2">Archivo</th>
              <th className="text-right px-5 py-2">Tamaño</th>
              <th className="text-right px-5 py-2">Chunks</th>
              <th className="text-left px-5 py-2">Embeddings</th>
              <th className="text-left px-5 py-2">Estado</th>
              <th className="text-right px-5 py-2">Acciones</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {filtered.map(d => {
              const Icon = iconos[d.tipo]
              return (
                <tr key={d.id} className="hover:bg-slate-50">
                  <td className="px-5 py-3">
                    <div className="flex items-center gap-3">
                      <div className={cn(
                        'w-9 h-9 rounded-lg flex items-center justify-center',
                        d.tipo === 'pdf' && 'bg-rose-50 text-rose-600',
                        d.tipo === 'md' && 'bg-blue-50 text-blue-600',
                        d.tipo === 'xlsx' && 'bg-emerald-50 text-emerald-600',
                        d.tipo === 'img' && 'bg-amber-50 text-amber-600',
                      )}>
                        <Icon className="w-4 h-4" />
                      </div>
                      <div>
                        <div className="font-medium text-navy-700">{d.nombre}</div>
                        <div className="text-[11px] text-slate-500">{d.subidoHace}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-5 py-3 text-right font-mono text-slate-700">{d.tamano}</td>
                  <td className="px-5 py-3 text-right font-mono text-slate-700">{d.chunks}</td>
                  <td className="px-5 py-3 w-48">
                    <Progress value={d.embeddings} max={d.chunks} color={d.estado === 'error' ? 'rose' : d.estado === 'indexando' ? 'amber' : 'emerald'} size="sm" />
                    <div className="text-[10px] text-slate-500 font-mono mt-0.5">{d.embeddings}/{d.chunks}</div>
                  </td>
                  <td className="px-5 py-3">
                    {d.estado === 'ok' && <Chip variant="emerald"><CheckCircle2 className="w-3 h-3 mr-1 inline" /> Indexado</Chip>}
                    {d.estado === 'indexando' && <Chip variant="amber"><Clock className="w-3 h-3 mr-1 inline" /> Indexando…</Chip>}
                    {d.estado === 'error' && <Chip variant="rose">Error · reintentar</Chip>}
                  </td>
                  <td className="px-5 py-3 text-right">
                    <div className="flex items-center justify-end gap-1">
                      <button onClick={() => reindex(d.id)} className="w-7 h-7 flex items-center justify-center rounded-md hover:bg-slate-100 text-slate-500" title="Reindexar">
                        <RefreshCw className="w-3.5 h-3.5" />
                      </button>
                      <button onClick={() => remove(d.id)} className="w-7 h-7 flex items-center justify-center rounded-md hover:bg-rose-50 text-rose-500" title="Borrar">
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </section>
    </div>
  )
}
